import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Sparkles } from "lucide-react";
import Navbar from "../components/Navbar";
import { GoogleSignInButton } from "../components/GoogleSignInButton";
import AuthSceneBackground from "../components/AuthSceneBackground";
import { useAuth } from "../context/AuthContext";

// Session-storage key for the inbound referral code. Captured on any
// landing-page URL with `?ref=` and consumed at registration time.
const REF_STORAGE_KEY = "cosmic_ref_code";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const { search } = useLocation();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [refCode, setRefCode] = useState(null);

  // On mount, harvest the ref code from one of three sources (in order):
  //   1. Current URL `?ref=<code>` (typical first-touch from a shared link).
  //   2. SessionStorage (if the visitor clicked Login → Register).
  //   3. Same-tab referrer URL parsed from `document.referrer`.
  // Whichever wins gets persisted to sessionStorage so it survives any
  // intra-app nav (cross-tab is ignored — sessionStorage is per-tab).
  useEffect(() => {
    let code = null;
    try {
      const params = new URLSearchParams(search);
      code = params.get("ref");
      if (!code) {
        code = sessionStorage.getItem(REF_STORAGE_KEY);
      }
      if (code) {
        sessionStorage.setItem(REF_STORAGE_KEY, code);
        setRefCode(code);
      }
    } catch { /* private mode — ref capture is best-effort */ }
  }, [search]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password.length < 6) {
      setErr("Password must be at least 6 characters.");
      return;
    }
    setLoading(true);
    const res = await register(email, password, name, refCode);
    setLoading(false);
    if (res.ok) {
      // Successful sign-up — clear the stashed code so a future logout/
      // re-register on the same tab doesn't re-credit the referrer.
      try { sessionStorage.removeItem(REF_STORAGE_KEY); } catch { /* noop */ }
      navigate("/levels");
    } else {
      setErr(res.error);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden">
      <div className="starfield" />
      <AuthSceneBackground />
      <Navbar />
      <main className="relative z-10 min-h-screen flex items-center justify-center px-6 pt-24 pb-12 fade-in">
        <div className="w-full max-w-md">
          <div className="relative">
            <div className="absolute -inset-1 border border-[#b026ff]/45 shadow-[0_0_26px_rgba(176,38,255,0.3)] pointer-events-none" />
            <form
              onSubmit={submit}
              className="relative glass p-8 border border-[#b026ff]/45 bg-[#05050a]/85 backdrop-blur-md"
              data-testid="register-form"
            >
              <div className="absolute -top-3 left-4 right-4 flex items-center justify-between">
                <div className="glass px-3 py-1 border border-[#b026ff]/50 flex items-center gap-2">
                  <Sparkles className="w-3 h-3 text-[#b026ff]" />
                  <span className="font-mono-cosmic text-[9px] tracking-[0.35em] text-[#b026ff] uppercase">
                    New Pilot
                  </span>
                </div>
                <div className="glass px-3 py-1 border border-[#00ffff]/40">
                  <span className="font-mono-cosmic text-[9px] tracking-[0.3em] text-[#00ffff]/90 uppercase">
                    Enlist · 00
                  </span>
                </div>
              </div>

              <div className="pt-4">
                <div className="font-mono-cosmic text-[11px] tracking-[0.35em] text-[#b026ff] uppercase mb-2">
                  // NEW.PILOT
                </div>
                <h1 className="font-heading font-black uppercase text-4xl tracking-tighter text-white mb-1">
                  Enlist now
                </h1>
                <p className="text-white/60 text-sm mb-8">
                  Create your callsign and save progress forever.
                </p>

                <label className="block mb-4">
                  <span className="font-mono-cosmic text-[10px] tracking-[0.3em] text-white/60 uppercase block mb-2">
                    Callsign
                  </span>
                  <input
                    type="text"
                    className="input-cosmic"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    minLength={1}
                    data-testid="register-name-input"
                  />
                </label>
                <label className="block mb-4">
                  <span className="font-mono-cosmic text-[10px] tracking-[0.3em] text-white/60 uppercase block mb-2">
                    Email
                  </span>
                  <input
                    type="email"
                    className="input-cosmic"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="register-email-input"
                  />
                </label>
                <label className="block mb-6">
                  <span className="font-mono-cosmic text-[10px] tracking-[0.3em] text-white/60 uppercase block mb-2">
                    Password
                  </span>
                  <input
                    type="password"
                    className="input-cosmic"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={6}
                    data-testid="register-password-input"
                  />
                </label>

                {err && (
                  <div
                    className="mb-4 px-3 py-2 border border-[#ff007f]/60 bg-[#ff007f]/10 text-[#ff007f] text-sm font-mono-cosmic"
                    data-testid="register-error"
                  >
                    {err}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="btn-neon w-full disabled:opacity-50"
                  data-testid="register-submit-btn"
                >
                  {loading ? "Enlisting..." : "Create Account"}
                </button>

                <GoogleSignInButton testId="register-google-btn" label="Sign up with Google" />

                <p className="mt-6 text-center text-sm text-white/60">
                  Already have one?{" "}
                  <Link
                    to="/login"
                    className="text-[#00ffff] hover:text-[#7afcff] transition"
                    data-testid="register-login-link"
                  >
                    Login
                  </Link>
                </p>
              </div>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
}
